import React, { useEffect, useRef, useState } from "react";
import { AutoFitText } from "@/components/AutoFitText";
import { resolveMediaUrl } from "@/components/FileUpload";
import { NEUTRAL_POSTER } from "@/lib/neutralPoster";
import { isHlsSrc, posterFor, attachHls, derivePreviewFromHls } from "@/lib/videoSource";
import { imageVariant } from "@/lib/mediaUrl";
import { storyImageSrc } from "@/lib/storyPrefetch";
import { StoryStickers, StoryDrawing, StoryBlurBackdrop, mediaTransformStyle, visibleStickers, hasLinkSticker } from "@/components/StoryMedia";
import { LinkPreviewCard, embedLabel } from "@/components/LinkPreviewCard";
import { SharedPostCard } from "@/components/SharedPostCard";
import { SharedListingCard } from "@/components/marketplace/SharedListingCard";
import { StoryCardFrame } from "@/components/story/StoryCardFrame";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { storyBgStyle } from "@/lib/gradients";
import { storyFontStyle, storyFontScale, useStoryFontReady } from "@/lib/storyFonts";
import { stripEmbedUrl } from "@/lib/linkText";
import { parseFirstUrl } from "@/lib/embeds";
import { api } from "@/lib/api";
import { openExternal } from "@/lib/externalLinks";

// Lot 664 — URL restée en texte (aperçu absent, ex. Facebook) : lien tapable dans le texte ; texte réduit à l'URL → carte.
const URL_RE = /(https?:\/\/[^\s]+)/gi;
export const linkifyStoryText = (text) => {
  if (!text || !/https?:\/\//i.test(text)) return text;
  const out = [];
  let last = 0, m, i = 0;
  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const href = m[0];
    out.push(<a key={`u${i++}`} href={href} className="underline break-all pointer-events-auto" onClick={(e) => { e.preventDefault(); e.stopPropagation(); openExternal(href).catch(() => {}); }} onPointerDown={(e) => e.stopPropagation()} data-testid="story-text-link">{href}</a>);
    last = m.index + href.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out.length ? out : text;
};
const urlOnlyEmbed = (text) => {
  const url = parseFirstUrl(text);
  if (!url || (text || "").trim() !== url) return null;
  let domain = ""; try { domain = new URL(url).hostname.replace(/^www\./, ""); } catch { /* noop */ }
  return { url, kind: "article", domain, title: null };
};

// Lot 665 — story dont l'URL est restée en texte (aperçu absent à la publication) : on récupère l'aperçu à l'affichage
// (titre, texte, image — Facebook/Instagram inclus) ; en cas d'échec la carte minimale (domaine + URL) reste.
const richCache = new Map();
const useRichEmbed = (fallback) => {
  const url = fallback?.url || null;
  const [rich, setRich] = useState(() => (url && richCache.get(url)) || null);
  useEffect(() => {
    if (!url || richCache.has(url)) { setRich((url && richCache.get(url)) || null); return undefined; }
    let alive = true;
    api.get("/link-preview", { params: { url } })
      .then(({ data }) => { const p = data?.preview; const v = p && (p.title || p.thumbnail || p.description) ? p : null; richCache.set(url, v); if (alive) setRich(v); })
      .catch(() => { richCache.set(url, null); });
    return () => { alive = false; };
  }, [url]);
  return rich;
};

// Rendu d'une story PARTAGÉ par le Fil (StoryBar) et la page d'église : une seule source de vérité
// pour le fond/média, le texte ajusté, la carte de lien, la publication partagée, le calque de dessin et les stickers.

// Fond d'une story texte (clé de dégradé, hex:#…, image illustrée) — défini dans lib/gradients, ré-exporté ici.
export { storyBgStyle };

// Texte principal sans l'URL brute quand la story porte une carte de lien.
const residualText = (st) => stripEmbedUrl(st?.text, st?.embed);

// Story « lien seul » : aucune bulle de texte visible une fois l'URL retirée → la carte est affichée plus grande.
const hasVisibleText = (st) => (st.layout === "free"
  ? visibleStickers(st.stickers, st.embed).some((s) => s.type === "text")
  : !!residualText(st));

// Vignette : carte de lien compacte (story texte sans sticker « lien », ou story photo/vidéo depuis le PC).
const TileLinkCard = ({ embed, testId, className = "" }) => (
  <div
    className={`relative w-full rounded-lg overflow-hidden shrink min-h-0 ${className}`}
    style={{ background: "rgba(0,0,0,0.55)", border: "1px solid rgba(255,255,255,0.25)", color: "#fff" }}
    data-testid={testId}
  >
    {embed.thumbnail && (
      <img src={embed.thumbnail} alt="" loading="lazy" decoding="async" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full aspect-video max-h-12 object-cover" data-testid={`${testId}-image`} />
    )}
    <div className="px-1.5 py-1 text-left">
      <div className="text-[6px] font-semibold uppercase tracking-wide truncate text-white/60">{embedLabel(embed)}</div>
      <div className="text-[8px] font-semibold leading-tight line-clamp-2" data-testid={`${testId}-title`}>{embed.title || embed.url}</div>
    </div>
  </div>
);

// Vignette (rangée du Fil, grille d'annonces d'église) : à placer dans un conteneur `relative` (ratio 9:16).
// `videoRef` : élément <video> exposé au parent (aperçu animé à tour de rôle dans la rangée du Fil).
export const StoryCover = ({ story: st, videoRef }) => {
  if (!st) return <div className="absolute inset-0" style={{ background: "var(--yalola-surface-alt)" }} />;
  if (st.media_type === "text") {
    const isImg = st.bg && st.bg.startsWith("/verse-bg/");
    const embed = st.embed?.url ? st.embed : null;
    const shown = residualText(st);
    // Carte de lien posée en sticker par l'auteur → rendue par StoryStickers à sa position, pas de carte centrée.
    const linkSticker = !!embed && hasLinkSticker(st.stickers);
    return (
      <div className={`absolute inset-0 flex flex-col items-center justify-center gap-1 px-2 ${embed && !linkSticker ? "pt-11 pb-9" : "p-2"}`} style={{ ...storyBgStyle(st.bg), containerType: "inline-size" }}>
        {isImg && <div className="absolute inset-0 bg-black/25 pointer-events-none" aria-hidden />}
        {shown && st.layout !== "free" && (
          <span className={`relative text-white font-bold text-center leading-tight ${embed ? "text-[9px] line-clamp-2" : "text-[10px] line-clamp-4"}`} style={{ textShadow: "0 1px 2px rgba(0,0,0,0.4)", ...storyFontStyle(st.font) }}>
            {shown}
          </span>
        )}
        {st.shared_post && <SharedPostCard sp={st.shared_post} compact interactive={false} testId={`story-tile-shared-${st.id}`} />}
        {st.shared_listing && <SharedListingCard sl={st.shared_listing} compact testId={`story-tile-listing-${st.id}`} />}
        {embed && !linkSticker && <TileLinkCard embed={embed} testId={`story-tile-link-card-${st.id}`} />}
        <StoryDrawing url={st.drawing_url} />
        <StoryStickers stickers={st.stickers} embed={embed} />
      </div>
    );
  }
  const cover = st.media_url ? resolveMediaUrl(st.media_url) : null;
  if (!cover) return <div className="absolute inset-0" style={{ background: "var(--yalola-surface-alt)" }} />;
  // Carte de lien d'une story photo/vidéo : sticker posé par l'auteur (téléphone) ou carte en bas (PC).
  const mediaEmbed = st.embed?.url ? st.embed : null;
  const mediaLinkSticker = !!mediaEmbed && hasLinkSticker(st.stickers);
  // Affiche (première image) des vidéos → jamais de cadre noir Android WebView pendant le chargement.
  const posterSrc = st.media_type === "video"
    ? (st.poster_url
        ? resolveMediaUrl(st.poster_url)
        : (cover.includes("cloudflarestream.com") ? cover.replace(/\/manifest\/video\.m3u8.*$/, "/thumbnails/thumbnail.jpg") : undefined))
    : undefined;
  // Vidéo transcodée Bunny → aperçu animé (preview.webp) servi par le CDN ; sinon <video> R2 ; erreur → affiche fixe.
  const bunnyPreview = st.media_type === "video" && st.hls_url ? derivePreviewFromHls(resolveMediaUrl(st.hls_url)) : null;
  const mediaEl = st.media_type === "video"
    ? (bunnyPreview
        ? <img src={bunnyPreview} onError={(e) => { e.currentTarget.src = posterSrc || NEUTRAL_POSTER; }} loading="lazy" decoding="async" alt="" className="story-tile-cover" style={mediaTransformStyle(st.media_transform)} data-testid="story-tile-preview" />
        : <video ref={videoRef} src={`${cover}#t=0.1`} poster={posterSrc || NEUTRAL_POSTER} className="story-tile-cover" style={mediaTransformStyle(st.media_transform)} muted playsInline loop preload="metadata" />)
    : <img src={cover} loading="lazy" decoding="async" alt="" className="story-tile-cover" style={mediaTransformStyle(st.media_transform)} />;
  return (
    <>
      {mediaEl}
      <StoryDrawing url={st.drawing_url} />
      <StoryStickers stickers={st.stickers} embed={mediaEmbed} />
      {mediaEmbed && !mediaLinkSticker && (
        <div className="absolute inset-x-1 bottom-1 flex" style={{ containerType: "inline-size" }}><TileLinkCard embed={mediaEmbed} testId={`story-tile-link-card-${st.id}`} /></div>
      )}
    </>
  );
};

// Scène de la visionneuse (remplit son parent `relative`) : texte ajusté dans la zone sûre
// (`safeTop` / `safeBottom` = hauteur de l'en-tête et de la barre du bas de la visionneuse hôte),
// vidéo HLS (hls.js) ou MP4, image, puis dessin + stickers par-dessus.
// Lot 628 — réseau lent : la vidéo MP4 ne démarre qu'avec ~3 s d'avance en tampon (ou après 2,5 s max),
// et un voyant s'affiche pendant les re-tamponnages → plus de lecture « par à-coups ».
const BUFFER_AHEAD_SEC = 3;
const BUFFER_MAX_WAIT_MS = 2500;

export const StoryStage = ({ story, videoRef, muted = true, paused = false, onVideoEnded, safeTop = "4.5rem", safeBottom = "8rem", testIdPrefix = "story-viewer" }) => {
  const nav = useNavigate();
  const { t } = useApp();
  const fontReady = useStoryFontReady(story?.font);
  // lot 665 : aperçu récupéré à l'affichage pour une story texte réduite à une URL sans aperçu stocké
  const textFallback = story?.media_type === "text" && !story?.embed?.url ? urlOnlyEmbed(residualText(story)) : null;
  const richEmbed = useRichEmbed(textFallback);
  const videoEl = useRef(null);
  const [buffering, setBuffering] = useState(false);
  const startedRef = useRef(false);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const setVideo = (el) => {
    videoEl.current = el;
    if (typeof videoRef === "function") videoRef(el);
    else if (videoRef) videoRef.current = el;
  };
  // Vidéo Bunny / Cloudflare HLS (pipeline unifié) : URL adaptative si prête, sinon MP4 R2 brut.
  const playSrc = story?.hls_url || story?.media_url || "";
  const isHls = story?.media_type === "video" && isHlsSrc(resolveMediaUrl(playSrc));
  useEffect(() => {
    const el = videoEl.current;
    if (!el || !isHls) return undefined;
    return attachHls(el, playSrc, { autoPlay: () => !paused });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.id, isHls]);

  // MP4 progressif : démarrage différé jusqu'à un tampon suffisant (canplaythrough / 3 s d'avance), 2,5 s max.
  useEffect(() => {
    startedRef.current = false;
    const el = videoEl.current;
    if (!el || isHls || story?.media_type !== "video") { setBuffering(false); return undefined; }
    setBuffering(true);
    let done = false;
    const start = () => {
      if (done) return;
      done = true; startedRef.current = true;
      setBuffering(false);
      if (!pausedRef.current) el.play().catch(() => {});
    };
    const aheadOk = () => {
      try {
        for (let i = 0; i < el.buffered.length; i++) {
          if (el.buffered.start(i) <= 0.2 && el.buffered.end(i) - el.currentTime >= Math.min(BUFFER_AHEAD_SEC, (el.duration || 99) - 0.3)) return true;
        }
      } catch { /* noop */ }
      return false;
    };
    const onProgress = () => { if (aheadOk()) start(); };
    const tm = setTimeout(start, BUFFER_MAX_WAIT_MS);
    el.addEventListener("canplaythrough", start);
    el.addEventListener("progress", onProgress);
    el.addEventListener("loadeddata", onProgress);
    try { el.load(); } catch { /* noop */ }
    return () => { done = true; clearTimeout(tm); el.removeEventListener("canplaythrough", start); el.removeEventListener("progress", onProgress); el.removeEventListener("loadeddata", onProgress); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story?.id, isHls]);

  if (!story) return null;
  if (story.media_type === "text") {
    const residual = residualText(story);
    // Lot 664 : texte réduit à une URL sans aperçu → carte de lien ; sinon URLs tapables dans le texte.
    const fallbackEmbed = !story.embed?.url ? urlOnlyEmbed(residual) : null;
    const embed = story.embed?.url ? story.embed : (richEmbed || fallbackEmbed);
    const shownText = fallbackEmbed ? "" : residual;
    const linkSticker = !!story.embed?.url && hasLinkSticker(story.stickers);
    const linkOnly = !!embed?.url && !linkSticker && !story.shared_post && !story.shared_listing && (fallbackEmbed ? true : !hasVisibleText(story));
    // anciennes stories (avant la zone sûre de l'éditeur) : stickers remappés sous l'en-tête / au-dessus de la barre
    const safeInset = story.layout === "free" && !story.stickers_safe ? { top: `calc(var(--sa-top, 0px) + ${safeTop})`, bottom: `calc(var(--sa-bottom, 0px) + ${safeBottom})` } : null;
    return (
      <div
        className="w-full h-full relative flex flex-col items-center justify-center gap-6 px-8"
        style={{ ...storyBgStyle(story.bg), paddingTop: `calc(var(--sa-top, 0px) + ${safeTop})`, paddingBottom: `calc(var(--sa-bottom, 0px) + ${safeBottom})` }}
        data-testid={`${testIdPrefix}-text`}
        data-link-only={linkOnly ? "1" : undefined}
        data-link-sticker={linkSticker ? "1" : undefined}
        data-link-fallback={fallbackEmbed ? "1" : undefined}
      >
        {story.bg?.startsWith("/verse-bg/") && <div className="absolute inset-0 bg-black/30" aria-hidden />}
        {shownText && story.layout !== "free" && (
          <div className={`relative w-full min-h-0 ${embed && !linkSticker ? "h-[40%]" : "flex-1"}`}>
            <AutoFitText text={shownText} render={linkifyStoryText} scale={storyFontScale(story.font, story.text_scale || 1)} refitKey={fontReady} minPx={16} maxRatio={0.12} maxPx={56} className="relative font-bold text-white" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.4)", ...storyFontStyle(story.font) }} testId={`${testIdPrefix}-text-fit`} />
          </div>
        )}
        {/* Lot 697 — toutes les cartes partagées (lien, publication, annonce) : même cadre, même largeur ; le corps laisse
            passer les gestes (précédent / suivant / pause), seul le bouton ouvre le contenu. */}
        {embed && !linkSticker && (
          <StoryCardFrame testId={`${testIdPrefix}-link`} cta={t("story.shared_open_link", "Ouvrir le lien")} onCta={() => openExternal(embed.url).catch(() => {})}>
            <LinkPreviewCard embed={embed} large={linkOnly} testId={`${testIdPrefix}-link-card`} />
          </StoryCardFrame>
        )}
        {story.shared_post && (
          <StoryCardFrame testId={`${testIdPrefix}-shared-post`} cta={story.shared_post.private ? null : t("story.shared_open", "Voir la publication")} onCta={() => nav(`/post/${story.shared_post.id}`)}>
            <SharedPostCard sp={story.shared_post} interactive={false} testId={`${testIdPrefix}-shared-post-card`} />
          </StoryCardFrame>
        )}
        {/* Lot 698 — `from=story` : la fiche signale l'ouverture avec cette provenance → compteur « depuis ta story » du vendeur. */}
        {story.shared_listing && (
          <StoryCardFrame testId={`${testIdPrefix}-shared-listing`} cta={t("market.see_listing", "Voir l'annonce")} onCta={() => nav(`/marketplace?item=${encodeURIComponent(story.shared_listing.id)}&from=story`)}>
            <SharedListingCard sl={story.shared_listing} testId={`${testIdPrefix}-shared-listing-card`} />
          </StoryCardFrame>
        )}
        <StoryDrawing url={story.drawing_url} testId={`${testIdPrefix}-drawing`} />
        <StoryStickers stickers={story.stickers} embed={story.embed} interactive safeInset={safeInset} testId={`${testIdPrefix}-stickers`} />
      </div>
    );
  }
  const poster = story.media_type === "video" ? posterFor(playSrc, story.poster_url) : null;
  // Carte de lien d'une story photo/vidéo : sticker déplaçable posé par l'auteur, sinon carte en bas (composeur PC).
  const mediaEmbed = story.embed?.url ? story.embed : null;
  const mediaLinkSticker = !!mediaEmbed && hasLinkSticker(story.stickers);
  return (
    <>
      <StoryBlurBackdrop src={story.media_type === "video" ? poster : imageVariant(story.media_url, 64)} testId={`${testIdPrefix}-blur`} />
      {story.media_type === "video" ? (
        <>
          <video
            key={story.id}
            ref={setVideo}
            src={isHls ? undefined : resolveMediaUrl(playSrc)}
            poster={poster || NEUTRAL_POSTER}
            autoPlay={isHls}
            preload="auto"
            muted={muted}
            playsInline
            onEnded={onVideoEnded}
            onWaiting={() => startedRef.current && setBuffering(true)}
            onStalled={() => startedRef.current && setBuffering(true)}
            onPlaying={() => setBuffering(false)}
            className="relative w-full h-full object-contain"
            style={mediaTransformStyle(story.media_transform)}
            data-testid={`${testIdPrefix}-video`}
            data-buffering={buffering ? "1" : "0"}
          />
          {buffering && (
            <div className="absolute inset-0 grid place-items-center pointer-events-none" data-testid={`${testIdPrefix}-buffering`}>
              <span className="h-9 w-9 rounded-full border-2 border-white/30 border-t-white animate-spin" />
            </div>
          )}
        </>
      ) : (
        <img src={storyImageSrc(story)} loading="eager" decoding="async" alt="" className="relative w-full h-full object-contain" style={mediaTransformStyle(story.media_transform)} data-testid={`${testIdPrefix}-image`} />
      )}
      <StoryDrawing url={story.drawing_url} testId={`${testIdPrefix}-drawing`} />
      <StoryStickers stickers={story.stickers} embed={mediaEmbed} interactive testId={`${testIdPrefix}-stickers`} />
      {mediaEmbed && !mediaLinkSticker && (
        <div className="absolute inset-x-0 z-10 px-6 flex justify-center" style={{ bottom: `calc(var(--sa-bottom, 0px) + ${safeBottom} + 0.5rem)` }} data-testid={`${testIdPrefix}-link`} data-media-link="1">
          <div className="w-full max-w-xs"><LinkPreviewCard embed={mediaEmbed} testId={`${testIdPrefix}-link-card`} /></div>
        </div>
      )}
    </>
  );
};
